import "./richComponentMessage.css";
import { useEffect } from "react";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { util } from "../helpers/common";
import { t } from "../awac/i18n";
import { localizeAgentText, localizeOptionLabel } from "../awac/agentTextL10n";

/**
 * RichComponentMessage
 * ---------------------------------------------------------------------------
 * Renders an AWAC (Adaptive Web Agent Component) envelope message.
 *
 * Flow:
 *  1. The agent sends a StaticContentMessage (formatType=Text) whose text is an
 *     AWAC JSON envelope (see helpers/conversationEntryUtil#getAwacEnvelope).
 *  2. conversationEntry.js detects it via isAwacMessage() and renders THIS
 *     component instead of a plain text bubble.
 *  3. This component:
 *      - lifts the envelope up to the parent so it can render the adaptive
 *        component in a SEPARATE gallery panel (onAwacRender(envelope)),
 *      - renders the agent's `text` line (if present) inside the normal chat
 *        bubble (the chat stream itself is left untouched — no inline gallery),
 *      - renders `options` as round-trip action buttons that send a normal
 *        text message back to the agent (onAwacAction), keeping the agent in
 *        the loop.
 *
 * The heavy adaptive component (FloorPlanShowcase) is NOT rendered inline here;
 * it is mounted by GalleryPanel as a separate docked section.
 *
 * @param {object} props
 * @param {object} props.conversationEntry
 * @param {Function} props.onAwacAction - (text) => Promise. Sends a reply to the agent.
 * @param {Function} props.onAwacRender - (envelope, templateName) => void. Opens the gallery panel.
 */
export default function RichComponentMessage({ conversationEntry, onAwacAction, onAwacRender }) {
    const envelope = ConversationEntryUtil.getAwacEnvelope(conversationEntry);
    const templateName = ConversationEntryUtil.getAwacTemplateName(conversationEntry);

    // Lift the envelope to the parent so the gallery renders in its own panel.
    useEffect(() => {
        if (envelope && typeof onAwacRender === "function") {
            onAwacRender(envelope, templateName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationEntry.messageId]);

    if (!envelope) {
        return null;
    }

    const introText = typeof envelope.text === "string" ? localizeAgentText(envelope.text, false) : "";
    const options = Array.isArray(envelope.options) ? envelope.options : [];

    // Template-aware label for the re-open link (the gallery auto-opens on
    // arrival; this link lets the prospect bring it back after closing it).
    // Labels live in the i18n tables (link.<TemplateName>) so they follow the
    // site language.
    const KNOWN_LINK_TEMPLATES = ["UnitCarousel", "NeighborhoodMap", "FloorPlanShowcase"];
    const galleryLinkLabel = KNOWN_LINK_TEMPLATES.indexOf(templateName) !== -1
        ? t(`link.${templateName}`)
        : t("link.gallery");

    function handleOptionClick(option) {
        if (typeof onAwacAction !== "function") {
            return;
        }
        // The text we send back: prefer an explicit `send` string, else the option label/name.
        const replyText = option.send || option.value || option.name || option.label || "";
        if (!replyText) {
            return;
        }
        onAwacAction(replyText);
    }

    return (
        <div className="awacMessageContainer">
            {introText ? <p className="awacIntroText">{introText}</p> : null}

            <button
                type="button"
                className="awacOpenGalleryLink"
                onClick={() => onAwacRender && onAwacRender(envelope, templateName)}>
                {galleryLinkLabel} &rarr;
            </button>

            {options.length ? (
                <div className="awacOptions">
                    {options.map((opt, i) => (
                        <button
                            key={`${util.generateUUID()}-${i}`}
                            type="button"
                            className="awacOptionButton"
                            onClick={() => handleOptionClick(opt)}>
                            {localizeOptionLabel(opt.name || opt.label || opt.send || "Select")}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
